import React from 'react'
import i18n from 'live/services/i18n'
import authorization from 'live/services/authorization'
import { ModalButton } from '../../modal-components-style'
import { RawActivityEvent, RawRunEvent } from '../../../utils/type'
import { Errors } from '../types'
import { DeleteConfirmationTubularModal, STATIC_DATA_STATES } from '../../chart-components'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import styled from 'styled-components'

interface ActivityFooterProps {
    runEvent: RawRunEvent
    event: RawActivityEvent
    rawTubularList: RawActivityEvent[]
    tubularIndex: number
    updateTubularIndex: (number: number) => void
    validateFields: (event: RawActivityEvent) => { pass: boolean; errors: Errors }
    updateTubularList: (event: RawActivityEvent, previousButtonClicked?: boolean) => void
    postActivities: () => void
    closeModal: () => void
    isStartButtonDisabled: boolean
    isModalDoubleSize: boolean
    scrollToTop: () => void
    isDeleteConfirmationModalOpen: boolean
    openDeleteConfirmationModal: () => void
    closeDeleteConfirmationModal: () => void
    hasChanged: boolean
}

const ActivityFooter = ({
    runEvent,
    rawTubularList,
    tubularIndex,
    updateTubularIndex,
    validateFields,
    event,
    updateTubularList,
    postActivities,
    closeModal,
    isStartButtonDisabled,
    isModalDoubleSize,
    scrollToTop,
    isDeleteConfirmationModalOpen,
    openDeleteConfirmationModal,
    closeDeleteConfirmationModal,
    hasChanged
}: ActivityFooterProps): JSX.Element => {
    const isLastTubular = !runEvent?.tubularsIntervalPerTime && runEvent?.finalMD?.value === event?.finalMD?.value
    const hasPermissionToEdit =
        authorization.superAdmin ||
        authorization.admin ||
        authorization.Permission.ADMIN ||
        authorization.Permission.OGPERFORMANCE_MANAGE_KPI ||
        authorization.Permission.OGPERFORMANCE_ADMINISTRATE_KPI

    return (
        <ActivityFooterContainer>
            {hasPermissionToEdit && !runEvent?.tubularsIntervalPerTime && rawTubularList && (
                <>
                    {event.id ? (
                        <ModalButton
                            styleType="action"
                            onClick={() => {
                                const { pass, errors } = validateFields(event)
                                if (pass) postActivities()
                            }}
                        >
                            {i18n('Save')}
                        </ModalButton>
                    ) : (
                        <>
                            {tubularIndex > 0 && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip style={{ zIndex: 9999 }} id="previous-button-tooltip">
                                            {i18n(
                                                'Attention, when clicking on the previous button, all the filling of this Tubular will be erased.'
                                            )}
                                        </Tooltip>
                                    }
                                >
                                    <ModalButton
                                        styleType="action"
                                        onClick={() => {
                                            updateTubularList(event, true)
                                            updateTubularIndex(-1)
                                        }}
                                    >
                                        <i className="far fa-arrow-left" />
                                        {i18n('Previous')}
                                        <i className="fas fa-exclamation-circle" />
                                    </ModalButton>
                                </OverlayTrigger>
                            )}
                            {isLastTubular ? (
                                <ModalButton
                                    style={{ display: 'flex' }}
                                    styleType="action"
                                    id="btn-start"
                                    disabled={isStartButtonDisabled}
                                    onClick={() => {
                                        const { pass, errors } = validateFields(event)
                                        if (pass) {
                                            updateTubularList(event)
                                            postActivities()
                                        }
                                    }}
                                >
                                    <i className="far fa-arrow-right"></i>
                                    {i18n('Start')}
                                </ModalButton>
                            ) : (
                                <ModalButton
                                    styleType="action"
                                    disabled={isStartButtonDisabled}
                                    onClick={() => {
                                        const { pass, errors } = validateFields(event)
                                        if (pass) {
                                            updateTubularList(event)
                                            updateTubularIndex(1)
                                            scrollToTop()
                                        }
                                    }}
                                >
                                    <i className="far fa-arrow-right"></i>
                                    {i18n('Next')}
                                </ModalButton>
                            )}
                        </>
                    )}
                </>
            )}

            {hasPermissionToEdit && runEvent?.tubularsIntervalPerTime && (
                <>
                    {!event.id ? (
                        // || ((extend || runEvent?.finalMD === endDepth) && !eventsFromSameRun)) && !event.finished
                        <ModalButton
                            styleType="action"
                            id="btn-start"
                            disabled={isStartButtonDisabled && isModalDoubleSize}
                            onClick={() => {
                                const { pass, errors } = validateFields(event)
                                if (pass) postActivities()
                            }}
                        >
                            {i18n('Start')}
                        </ModalButton>
                    ) : event.state !== STATIC_DATA_STATES.FINISHED ? (
                        <ModalButton
                            styleType="action"
                            onClick={() => {
                                const { pass, errors } = validateFields(event)
                                if (pass && hasChanged === true) {
                                    openDeleteConfirmationModal()
                                } else {
                                    postActivities()
                                }
                            }}
                        >
                            {i18n('Finish')}
                        </ModalButton>
                    ) : (
                        <ModalButton
                            styleType="action"
                            onClick={() => {
                                const { pass, errors } = validateFields(event)
                                if (pass && hasChanged === true) {
                                    openDeleteConfirmationModal()
                                } else {
                                    postActivities()
                                }
                            }}
                        >
                            {i18n('Save')}
                        </ModalButton>
                    )}
                </>
            )}

            <ModalButton styleType="cancel" onClick={closeModal}>
                {hasPermissionToEdit ? i18n('Cancel') : i18n('Close')}
            </ModalButton>

            {event?.state === STATIC_DATA_STATES.STARTED_IN_EXECUTION && (
                <DeleteConfirmationTubularModal
                    isOpen={isDeleteConfirmationModalOpen}
                    closeModal={closeDeleteConfirmationModal}
                    confirmDeletion={postActivities}
                />
            )}
            {event?.state === STATIC_DATA_STATES.FINISHED && (
                <DeleteConfirmationTubularModal
                    isOpen={isDeleteConfirmationModalOpen}
                    closeModal={closeDeleteConfirmationModal}
                    confirmDeletion={postActivities}
                />
            )}
        </ActivityFooterContainer>
    )
}

const ActivityFooterContainer = styled.div`
    display: flex;
    padding: 8px 40% 8px 20px;
    gap: 8px;
`

export default ActivityFooter
